import React, { useEffect, useRef } from "react";
import { Bot, Sparkles } from "lucide-react";

import { cn } from "../../utils/tailwindUtils";
import CopilotMessage from "./CopilotMessage";
import { COPILOT_GREETING_HINT } from "./constants";

/** Three-dot "thinking" indicator, styled like an incoming bubble. */
const ThinkingBubble = ({ compact }) => (
    <div className="flex items-start gap-2">
        <div
            className={cn(
                "flex shrink-0 items-center justify-center rounded-lg bg-sidebar text-white",
                compact ? "h-7 w-7" : "h-9 w-9"
            )}
        >
            <Bot className={compact ? "h-3.5 w-3.5" : "h-4 w-4"} />
        </div>
        <div
            className={cn(
                "flex items-center gap-2 rounded-xl rounded-bl-sm border border-border bg-muted/50 px-3.5 py-3",
                compact ? "text-xs" : "text-[13px]"
            )}
        >
            <span className="flex gap-1">
                {[0, 150, 300].map((delay) => (
                    <span
                        key={delay}
                        className="h-1.5 w-1.5 animate-bounce rounded-full bg-sidebar-primary"
                        style={{ animationDelay: `${delay}ms` }}
                    />
                ))}
            </span>
            <span className="text-muted-foreground">Checking your HR records…</span>
        </div>
    </div>
);

const EmptyState = ({ userName, compact }) => (
    <div className="m-auto max-w-md px-4 text-center">
        <div
            className={cn(
                "mx-auto mb-4 flex items-center justify-center rounded-2xl bg-sidebar text-white shadow-md",
                compact ? "h-12 w-12" : "h-16 w-16"
            )}
        >
            <Sparkles className={compact ? "h-6 w-6" : "h-8 w-8"} />
        </div>
        <h3
            className={cn(
                "font-semibold text-foreground",
                compact ? "text-sm" : "text-lg"
            )}
        >
            {userName ? `Hi ${userName}, how can I help?` : "How can I help you today?"}
        </h3>
        <p
            className={cn(
                "mt-1.5 leading-relaxed text-muted-foreground",
                compact ? "text-xs" : "text-[13px]"
            )}
        >
            {COPILOT_GREETING_HINT}
        </p>
    </div>
);

/**
 * The scrolling message list, shared by the docked widget and the full page.
 * Auto-scrolls to the newest turn, including while an answer is pending.
 */
const CopilotThread = ({
    messages,
    sending,
    loading,
    userName,
    feedbackState,
    onRate,
    compact = false,
    className,
}) => {
    const endRef = useRef(null);

    useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, [messages, sending]);

    const isEmpty = !messages.length && !sending && !loading;

    return (
        <div
            className={cn(
                "flex flex-1 flex-col overflow-y-auto",
                compact ? "gap-3 p-3" : "gap-4 p-4",
                isEmpty && "justify-center",
                className
            )}
        >
            {loading ? (
                <div className="m-auto flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-sidebar border-t-transparent" />
                    Loading conversation…
                </div>
            ) : isEmpty ? (
                <EmptyState userName={userName} compact={compact} />
            ) : (
                messages.map((message) => (
                    <CopilotMessage
                        key={message.id}
                        message={message}
                        compact={compact}
                        feedback={feedbackState?.[message.id]}
                        onRate={onRate}
                    />
                ))
            )}

            {sending && <ThinkingBubble compact={compact} />}
            <div ref={endRef} />
        </div>
    );
};

export default CopilotThread;
